/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta


// Función para crear un anuncio
const createAd = async (adData: {
    image: string;
    link: string;
    adType: string;
    startDate: string;
    endDate: string;
    userSegment: string;
    allowedCountries: string[];
    activeHours: string[];
    weight: number;
}) => {
    const response = await axiosInstance.post('/home-ads/ads/', adData);
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear un anuncio
export const useCreateAd = () => {
    const mutation = useMutation({
        mutationFn: createAd, // Función para crear el anuncio
        onSuccess: (data) => {
            toast.success(data.message || "Anuncio creado con éxito.");
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Error desconocido";

            toast.error(errorMessage); // Mostrar solo el mensaje del backend
            console.error("Error al crear el anuncio:", errorMessage);
        },
    });

    return {
        ...mutation,
        isLoading: mutation.isPending, // Estado de carga de la mutación
        isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
    };
};




// Función para obtener los anuncios paginados
const fetchAds = async ({ page = 1, limit = 10 }: { page?: number; limit?: number }) => {
    const response = await axiosInstance.get('/home-ads/ads/', {
        params: { page, limit },
    });
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar anuncios paginados
export const useGetAds = (page: number, limit: number = 10) => {
    const { data, error, isLoading, isError, refetch } = useQuery({
        queryKey: ['ads', page, limit], // React Query maneja los datos en caché según estos parámetros
        queryFn: async () => {
            try {
                return await fetchAds({ page, limit });
            } catch (error: any) {
                toast.error(`Error al obtener los anuncios: ${error.response?.data?.message || error?.message || 'Error desconocido'}`);
                console.error('Error al obtener los anuncios:', error);
                throw error;
            }
        },
        refetchOnWindowFocus: false, // Evita refetch automático al cambiar de pestaña
    });

    return {
        ads: data?.ads || [], // Lista de anuncios
        pagination: data?.pagination || {}, // Datos de paginación
        isLoading, // Estado de carga
        isError, // Si hubo error
        error, // Detalles del error
        refetch, // Función para volver a cargar los datos
    };
};




// Función para obtener el anuncio por ID
const fetchAdById = async (adId: string) => {
    const response = await axiosInstance.get(`/home-ads/ads/${adId}`);
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para obtener el anuncio por ID
export const useGetAdById = (adId: string) => {
    const { data, error, isLoading, isError } = useQuery({
        queryKey: ['ad', adId], // Identificador único para la consulta, que incluye el ID
        queryFn: async () => {
            try {
                return await fetchAdById(adId);
            } catch (error: any) {
                toast.error(`Error al obtener el anuncio: ${error.response?.data?.message || 'Error desconocido'}`);
                console.error('Error al obtener el anuncio:', error);
                throw error;
            }
        },
        refetchOnWindowFocus: false, // Evita que se vuelva a cargar cuando el foco de la ventana cambie
    });

    return {
        ad: data?.ad || null, // Anuncio encontrado
        isLoading,            // Estado de carga
        isError,              // Si hubo error
        error,                // Detalles del error
    };
};



// Función para eliminar el anuncio por ID
const deleteAdById = async (adId: string) => {
    const response = await axiosInstance.delete(`/home-ads/ads/${adId}`);
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para eliminar un anuncio por ID
export const useDeleteAdById = (adId: string) => {
    const { mutate, isLoading, isError, error } = useMutation({
        mutationKey: ['delete-ad', adId], // Identificador único para la consulta de eliminación
        mutationFn: async () => {
            try {
                return await deleteAdById(adId);
            } catch (error: any) {
                toast.error(`Error al eliminar el anuncio: ${error.response?.data?.message || 'Error desconocido'}`);
                console.error('Error al eliminar el anuncio:', error);
                throw error;
            }
        },
    });

    return {
        mutate,     // Función para ejecutar la mutación
        isLoading,  // Estado de carga
        isError,    // Si hubo error
        error,      // Detalles del error
    };
};



// Función para calcular los días en los que se mostrará el anuncio
const calculateAdDays = async (budget: number) => {
    const response = await axiosInstance.post('/home-ads/calculate-days', { budget });
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para calcular los días en los que se mostrará el anuncio
export const useCalculateAdDays = () => {
    const { mutate, isLoading, isError, error, data } = useMutation({
        mutationKey: ['calculate-ad-days'], // Identificador único para la mutación
        mutationFn: async (budget: number) => {
            try {
                return await calculateAdDays(budget); // Llama a la función que calcula los días
            } catch (error: any) {
                toast.error(`Error al calcular los días del anuncio: ${error.response?.data?.message || 'Error desconocido'}`);
                console.error('Error al calcular los días del anuncio:', error);
                throw error;
            }
        },
    });

    return {
        mutate,       // Función para ejecutar la mutación
        isLoading,    // Estado de carga
        isError,      // Si hubo error
        error,        // Detalles del error
        data,         // Datos de la respuesta (número de días)
    };
};



// Función para aprobar el anuncio con el depósito
const approveAdWithDeposit = async (adId: string, BudgetDepositAmount: number) => {
    const response = await axiosInstance.post(`/home-ads/ads/${adId}/pay`, { BudgetDepositAmount });
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para aprobar el anuncio con el depósito
export const useApproveAdWithDeposit = () => {
    const { mutate, isLoading, isError, error, data } = useMutation({
        mutationKey: ['approve-ad-with-deposit'], // Identificador único para la mutación
        mutationFn: async ({ adId, BudgetDepositAmount }: { adId: string; BudgetDepositAmount: number }) => {
            try {
                return await approveAdWithDeposit(adId, BudgetDepositAmount); // Llama a la función para aprobar el anuncio
            } catch (error: any) {
                toast.error(`Error al aprobar el anuncio: ${error.response?.data?.message || 'Error desconocido'}`);
                console.error('Error al aprobar el anuncio:', error);
                throw error;
            }
        },
    });

    return {
        mutate,       // Función para ejecutar la mutación
        isLoading,    // Estado de carga
        isError,      // Si hubo error
        error,        // Detalles del error
        data,         // Datos de la respuesta (mensaje de éxito o error)
    };
};


// Función para obtener los anuncios paginados
const fetchAdsOne = async ({ page = 1, limit = 10 }: { page?: number; limit?: number }) => {
    const response = await axiosInstance.get('/home-ads/ads/selection/listing', {
        params: { page, limit },
    });
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar anuncios paginados
export const useGetAdsOne = (page: number, limit: number = 10) => {
    const { data, error, isLoading, isError, refetch } = useQuery({
        queryKey: ['ads', page, limit], // React Query maneja los datos en caché según estos parámetros
        queryFn: async () => {
            try {
                return await fetchAdsOne({ page, limit });
            } catch (error: any) {
                toast.error(`Error al obtener los anuncios: ${error.response?.data?.message || error?.message || 'Server is Down'}`);
                console.error('Error al obtener los anuncios:', error);
                throw error;
            }
        },
        refetchOnWindowFocus: true, // Evita refetch automático al cambiar de pestaña
        refetchInterval: 50000,
        refetchOnReconnect: true
    });

    return {
        ads: data || [], // Lista de anuncios
        pagination: data?.pagination || {}, // Datos de paginación
        isLoading, // Estado de carga
        isError, // Si hubo error
        error, // Detalles del error
        refetch, // Función para volver a cargar los datos
    };
};



// Función para realizar el seguimiento del clic en un anuncio
const fetchAdClickTracking = async (id: string) => {
    const response = await axiosInstance.post(`/home-ads/track-click/${id}`);
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para hacer seguimiento del clic en un anuncio
export const useTrackAdClick = (id: string) => {
    const { data, error, isLoading, isError, refetch } = useQuery({
        queryKey: ['adClickTracking', id], // React Query maneja los datos en caché según el ID del anuncio
        queryFn: async () => {
            try {
                return await fetchAdClickTracking(id);
            } catch (error: any) {
                toast.error(`Error al registrar el clic: ${error.response?.data?.message || error?.message || 'Error desconocido'}`);
                console.error('Error al registrar el clic en el anuncio:', error);
                throw error;
            }
        },
        refetchOnWindowFocus: false, // Evita refetch automático al cambiar de pestaña
    });

    return {
        trackData: data?.ad || null, // Datos del anuncio después de hacer clic
        isLoading, // Estado de carga
        isError, // Si hubo error
        error, // Detalles del error
        refetch, // Función para volver a cargar los datos
    };
};


// Función para obtener las métricas de los anuncios
const fetchAdMetrics = async () => {
    const response = await axiosInstance.get(`/home-ads/ads/show/metrics`, {
    });
    return response.data; // Devuelve los datos de la respuesta
};

// Hook para obtener las métricas de los anuncios
export const useGetAdMetrics = (userId: string) => {
    const { data, error, isLoading, isError, refetch } = useQuery({
        queryKey: ['adMetrics', userId], // React Query maneja los datos en caché según el ID del usuario
        queryFn: async () => {
            try {
                return await fetchAdMetrics();
            } catch (error: any) {
                toast.error(`Error al obtener las métricas de los anuncios: ${error.response?.data?.message || error?.message || 'Error desconocido'}`);
                console.error('Error al obtener las métricas de los anuncios:', error);
                throw error;
            }
        },
        refetchOnWindowFocus: false, // Evita refetch automático al cambiar de pestaña
    });

    return {
        metrics: data?.metrics || [], // Datos de las métricas de los anuncios
        totalClicks: data?.totalClicks || 0, // Total de clics
        totalImpressions: data?.totalImpressions || 0, // Total de impresiones
        averageCTR: data?.averageCTR || 0, // CTR promedio
        countryClicks: data?.countryClicks || {}, // Clics por país
        deviceClicks: data?.deviceClicks || {}, // Clics por dispositivo
        isLoading, // Estado de carga
        isError, // Si hubo error
        error, // Detalles del error
        refetch, // Función para volver a cargar los datos
    };
};
